import {
  APIAttachment,
  APIComponentAttributes,
  APIEntityType,
  APIFieldAttributes,
  APIModelAttributes,
  APIReferenceAttributes,
  APITagAttributes,
  ArdoqBlob,
  ArrowType,
  PermissionAccessLevel,
} from './apiTypes';
import { Entity, Versioned, VersionedEntity } from './coreTypes';
import { APIDashboardAttributes } from './dashboardAPITypes';
import { APIPresentationAttributes } from './presentationAPITypes';
import {
  DataSourceType,
  DateInterval,
  FilterExpression,
  FilterValue,
  NumberInterval,
  Report,
} from './reportAPITypes';
import { APISlideAttributes } from './slideApiTypes';
import { APISurveyAttributes } from './surveyApiTypes/surveyApiTypes';
import {
  APIBroadcastTask,
  APIChangeApprovalTask,
  APITaskAttributes,
  APITaskType,
} from './taskApiTypes';
import { UnpersistedEntity } from './unpersistedEntityTypes';
import { APIViewpointAttributes } from './viewpointApiTypes';
import { APIWorkspaceAttributes } from './workspaceAPITypes';

const arrowTypeValues = new Set(Object.values(ArrowType));

export const isArrowType = (type: unknown): type is ArrowType =>
  arrowTypeValues.has(type as ArrowType);

export const isObject = (type: unknown): type is Record<string, unknown> => {
  return type instanceof Object;
};

export const isNumber = (type: unknown): type is number =>
  typeof type === 'number';
export const isDate = (candidate: unknown): candidate is Date =>
  !isNull(candidate) &&
  !isUndefined(candidate) &&
  Object.prototype.toString.call(candidate) === '[object Date]';
const isNull = (candidate: unknown): candidate is null => candidate === null;
const isUndefined = (candidate: unknown): candidate is undefined =>
  candidate === undefined;
const isString = (candidate: unknown): candidate is string =>
  typeof candidate === 'string';
const isBoolean = (candidate: unknown): candidate is boolean =>
  typeof candidate === 'boolean';

const isOf = (candidate: ArdoqBlob, type: APIEntityType) =>
  ('entity-type' in candidate.ardoq &&
    candidate.ardoq['entity-type'] === type) ||
  ('entityType' in candidate.ardoq && candidate.ardoq.entityType === type);

export const isEntity = (candidate: unknown): candidate is Entity =>
  isObject(candidate) && typeof candidate._id === 'string';

export const isVersioned = (candidate: unknown): candidate is Versioned =>
  isObject(candidate) && typeof candidate._version === 'number';

export const isVersionedEntity = (
  candidate: unknown
): candidate is VersionedEntity =>
  isEntity(candidate) && isVersioned(candidate);

const isArdoqEntity = (candidate: unknown): candidate is ArdoqBlob & Entity =>
  isObject(candidate) && isObject(candidate.ardoq) && isEntity(candidate);

export const getEntityType = (candidate: any): APIEntityType | null => {
  if (!candidate || !candidate.ardoq) {
    return null;
  }
  if ('entity-type' in candidate.ardoq) {
    return candidate.ardoq['entity-type'];
  }
  if ('entityType' in candidate.ardoq) {
    return candidate.ardoq.entityType;
  }
  return null;
};

export const isAttachment = (
  candidate: unknown
): candidate is APIAttachment => {
  return isArdoqEntity(candidate) && isOf(candidate, APIEntityType.ATTACHMENT);
};

export const isComponent = (
  candidate: unknown
): candidate is APIComponentAttributes => {
  return isArdoqEntity(candidate) && isOf(candidate, APIEntityType.COMPONENT);
};

export const isDashboard = (
  candidate: unknown
): candidate is APIDashboardAttributes => {
  return isArdoqEntity(candidate) && isOf(candidate, APIEntityType.DASHBOARD);
};

export const isField = (
  candidate: unknown
): candidate is APIFieldAttributes => {
  return isArdoqEntity(candidate) && isOf(candidate, APIEntityType.FIELD);
};

export const isModel = (
  candidate: unknown
): candidate is APIModelAttributes => {
  return isArdoqEntity(candidate) && isOf(candidate, APIEntityType.MODEL);
};

export const isPresentation = (
  candidate: unknown
): candidate is APIPresentationAttributes => {
  return (
    isArdoqEntity(candidate) && isOf(candidate, APIEntityType.PRESENTATION)
  );
};

export const isReference = (
  candidate: unknown
): candidate is APIReferenceAttributes => {
  return isArdoqEntity(candidate) && isOf(candidate, APIEntityType.REFERENCE);
};

export const isReport = (candidate: unknown): candidate is Report => {
  return isArdoqEntity(candidate) && isOf(candidate, APIEntityType.REPORT);
};

export const isSlide = (
  candidate: unknown
): candidate is APISlideAttributes => {
  return isArdoqEntity(candidate) && isOf(candidate, APIEntityType.SLIDE);
};

export const isTag = (candidate: unknown): candidate is APITagAttributes => {
  return isArdoqEntity(candidate) && isOf(candidate, APIEntityType.TAG);
};

export const isTraversal = (
  candidate: unknown
): candidate is APIViewpointAttributes => {
  return isArdoqEntity(candidate) && isOf(candidate, APIEntityType.TRAVERSAL);
};

export const isWorkspace = (
  candidate: unknown
): candidate is APIWorkspaceAttributes => {
  return isArdoqEntity(candidate) && isOf(candidate, APIEntityType.WORKSPACE);
};

export const isAdvancedSearch = (
  datasource: unknown
): datasource is DataSourceType.ADVANCED_SEARCH =>
  datasource === DataSourceType.ADVANCED_SEARCH;

export const isGremlinSearch = (
  datasource: unknown
): datasource is DataSourceType.GREMLIN_SEARCH =>
  datasource === DataSourceType.GREMLIN_SEARCH;

export const isPersistedSurvey = (
  survey: APISurveyAttributes | UnpersistedEntity<APISurveyAttributes>
): survey is APISurveyAttributes => {
  return isEntity(survey);
};

export const isPublishedSurvey = (
  survey: APISurveyAttributes | UnpersistedEntity<APISurveyAttributes>
): survey is APISurveyAttributes => {
  return isPersistedSurvey(survey) && survey.published;
};

export const isChangeApprovalTask = (
  task: APITaskAttributes
): task is APIChangeApprovalTask =>
  task.type === APITaskType.SURVEY_CHANGE_APPROVAL;

export const isBroadcastTask = (
  task: APITaskAttributes
): task is APIBroadcastTask => task.type === APITaskType.BROADCAST_TASK;

export const isNumberInterval = (
  candidate: unknown
): candidate is NumberInterval =>
  isObject(candidate) &&
  'start' in candidate &&
  'end' in candidate &&
  (isNumber(candidate.start) || isNull(candidate.start)) &&
  (isNumber(candidate.end) || isNull(candidate.end));

export const isDateInterval = (candidate: unknown): candidate is DateInterval =>
  isObject(candidate) &&
  'start' in candidate &&
  'end' in candidate &&
  (isDate(candidate.start) || isNull(candidate.start)) &&
  (isDate(candidate.end) || isNull(candidate.end));

export const isFilterValue = (candidate: unknown): candidate is FilterValue =>
  isString(candidate) ||
  isNumber(candidate) ||
  isBoolean(candidate) ||
  isNumberInterval(candidate) ||
  isNull(candidate);

export const isFilterExpression = (
  candidate: unknown
): candidate is FilterExpression =>
  isObject(candidate) && 'value' in candidate && isFilterValue(candidate.value);

export const isFilterExpressionArray = (
  arr: unknown
): arr is FilterExpression[] => {
  if (!Array.isArray(arr)) {
    return false;
  }

  if (arr.some(v => !isFilterExpression(v))) {
    return false;
  }

  return true;
};

const VALID_PERMISSION_ACCESS_LEVELS = new Set(
  Object.values(PermissionAccessLevel)
);

export const isPermissionAccessLevel = (
  candidate: unknown
): candidate is PermissionAccessLevel => {
  return (
    isString(candidate) &&
    VALID_PERMISSION_ACCESS_LEVELS.has(candidate as PermissionAccessLevel)
  );
};
